import React, { useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const styles = {
    paper: {
        p: { xs: 2, md: 3 },
        mb: 3,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
    },
    title: {
        mb: 1,
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fit, minmax(150px, 1fr))' },
        gap: { xs: 1.5, md: 2 },
        mb: 3,
    },
    infoItem: {
        '& .label': {
            color: 'text.secondary',
            fontSize: '0.75rem',
            mb: 0.5,
        },
        '& .value': {
            fontSize: '0.875rem',
        },
    },
    logsContainer: {
        bgcolor: 'background.default',
        borderRadius: 1,
        p: { xs: 1.5, md: 2 },
        maxHeight: { xs: 300, md: 400 },
        overflow: 'auto',
    },
    logsTitle: {
        mb: 1,
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    logs: {
        fontFamily: 'monospace',
        fontSize: { xs: '0.6875rem', md: '0.75rem' },
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        color: 'text.secondary',
        m: 0,
    },
};

const statusColors = {
    pending: 'warning',
    running: 'info',
    success: 'success',
    failed: 'error',
    cancelled: 'default',
};

export default function BuildLogs({ build, onClose }) {
    const logsRef = useRef(null);

    useEffect(() => {
        if (logsRef.current) {
            logsRef.current.scrollTop = logsRef.current.scrollHeight;
        }
    }, [build?.logs]);

    if (!build) return null;

    const formatLogs = () => {
        if (!build.logs?.length) return '로그가 없습니다.';
        return build.logs
            .map(log => `[${new Date(log.timestamp).toLocaleTimeString()}] ${log.message}`)
            .join('\n');
    };

    return (
        <Paper sx={styles.paper}>
            <Box sx={styles.header}>
                <Box>
                    <Typography variant="h2" sx={styles.title}>
                        현재 빌드
                    </Typography>
                    <Chip
                        label={build.status}
                        color={statusColors[build.status] || 'default'}
                        size="small"
                    />
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={styles.infoGrid}>
                <Box sx={styles.infoItem}>
                    <Typography className="label">제품</Typography>
                    <Typography className="value">{build.productName || '-'}</Typography>
                </Box>
                <Box sx={styles.infoItem}>
                    <Typography className="label">태그</Typography>
                    <Typography className="value">{build.tag || '-'}</Typography>
                </Box>
                <Box sx={styles.infoItem}>
                    <Typography className="label">프로파일</Typography>
                    <Typography className="value">{build.profile || '-'}</Typography>
                </Box>
                <Box sx={styles.infoItem}>
                    <Typography className="label">모드</Typography>
                    <Typography className="value">{build.mode}</Typography>
                </Box>
                <Box sx={styles.infoItem}>
                    <Typography className="label">요청자</Typography>
                    <Typography className="value">{build.requestedBy}</Typography>
                </Box>
            </Box>

            <Box sx={styles.logsContainer} ref={logsRef}>
                <Typography sx={styles.logsTitle}>빌드 로그</Typography>
                <Box component="pre" sx={styles.logs}>
                    {formatLogs()}
                </Box>
            </Box>
        </Paper>
    );
}
